import {Component, Input, OnInit} from '@angular/core';
import {GeneralService} from "../../../../services/generalService";
import {RouteService} from "../../../../services/routeService";
import {NotificationService} from "../../../../services/notificationService";
import {DICTIONARY} from "../../../../dictionary";
import _ from "lodash";
import {LookAndFeelService} from "../../../../services/lookAndFeelService";

@Component({
  selector: 'threat-simulation-campaign-recipients-summary-component',
  templateUrl: './threat-simulation-campaign-recipients-summary.component.html',
})
export class ThreatSimulationCampaignRecipientsSummaryComponent implements OnInit {

	@Input() currentCampaign;
	constructor(public gs:GeneralService,
				private rs:RouteService,
				public lfs:LookAndFeelService,
				private ns:NotificationService) {
	}

	dic = DICTIONARY;
	filterData;
	isTrainingCampaign;
	campaignSummary: any;
	getSummaryInProcess: boolean = false;
	recipientsEventsTable: any;

	// recipient status is split into delivery states (Queued / Not Delivered) and outcome states (Fail / In Progress / Pass / Incomplete).
	// "Pass" is final: it is only assigned once the campaign has ended. before that a delivered recipient with no failure is "In Progress".
	recipientStatusDisplay = {
		'Pass': {icon: 'fa-check-circle', color: 'darkgreen', graphColor: '#2E7D32', tooltip: 'Pass - the recipient completed the campaign without failing'},
		'Fail': {icon: 'fa-exclamation-triangle', color: 'orange', graphColor: '#ff0211',tooltip: 'Fail - the recipient clicked the link, downloaded the attachment or did not pass the quiz'},
		'In Progress': {icon: 'fa-hourglass-half', color: '#3984e5', graphColor: '#1976D2',tooltip: 'In Progress - the email was delivered and the recipient has not responded yet. A final status will be set when the campaign ends'},
		'Queued for Delivery': {icon: 'fa-hourglass-start', color: 'dimgray', graphColor: '#757575',tooltip: 'Queued - the email was not sent yet. Delivery is spread across the campaign dispatch window'},
		'Not Delivered': {icon: 'fa-ban', color: 'red', graphColor: '#c32fd3',tooltip: 'Not Delivered - the email bounced or failed to send'},
		'Incomplete': {icon: 'fa-hourglass-end', color: 'orange', graphColor: '#FFC107',tooltip: 'Incomplete - the campaign ended without the recipient acknowledging the material'}
	};

	ngOnInit() {
		this.isTrainingCampaign = this.isCampaignType(this.currentCampaign, this.dic.CONSTANTS.threatSimulationType.materials.value) ||
			this.isCampaignType(this.currentCampaign, this.dic.CONSTANTS.threatSimulationType.quiz.value);

		this.getSummaryData();
		this.initFilters();
	}

	initFilters = () => {
		this.filterData = {
			filterType: 'threatSimulationRecipientsSummary',
			filters: {
				status: Object.keys(this.recipientStatusDisplay),
			},
			initFiltersFunction: this.initFilters,
		};
	}

	getSummaryData = () => {
		if (this.currentCampaign.campaigns?.length) {
			const campaignIDs = _.map(this.currentCampaign.campaigns, '_id');
			this.getCampaignGroupSummary(campaignIDs);
		}
		else {
			this.getCampaignSummary();
		}
	}

	getCampaignSummary = () => {
		this.getSummaryInProcess = true;
		this.rs.getCampaignSummary(this.currentCampaign._id).then((response) => {
			this.campaignSummary = response;

			this.prepareGeneralEventsGraph();
			this.prepareRecipientsTable();
			this.getSummaryInProcess = false;
		}, (err) => {
			this.getSummaryInProcess = false;
		});
	}

	getCampaignGroupSummary = (campaignIDs) => {
		this.getSummaryInProcess = true;

		const queryParams = {arr: campaignIDs.join(',')};

		this.rs.getCampaignGroupSummary(queryParams).then((response) => {
			this.campaignSummary = response;

			this.prepareGeneralEventsGraph();
			this.prepareRecipientsTable();
			this.getSummaryInProcess = false;
		}, (err) => {
			this.getSummaryInProcess = false;
		});
	}

	// campaign end = send time + duration. when it cannot be derived (e.g. grouped campaigns without an email record)
	// we fall back to "ended" which preserves the legacy behavior (sent without failure = Pass)
	getCampaignEndTime() {
		const sendTime = this.currentCampaign.email_id?.start_sending_time || this.currentCampaign.email_id?.scheduled_time;
		if (!sendTime) {
			return null;
		}
		const endTime = new Date(sendTime);
		endTime.setDate(endTime.getDate() + (this.currentCampaign.email_id?.advanced?.secure?.expired_days || 7));
		return endTime;
	}

	prepareRecipientsTable() {
		this.recipientsEventsTable = {recipients: []};
		this.campaignSummary.recipients?.forEach((recipientObj) => {
			const quizResult = recipientObj.track_events.find(itm => itm.name === this.dic.EVENTS.eventQuiz.name)?.raw_data;

			const recipientInfo: any = {
				email: recipientObj.email,
				status: recipientObj.status,
				sent: recipientObj.track_events.find(itm => itm.name === this.dic.EVENTS.eventSent.name),
				sentFailed: recipientObj.track_events.find(itm => itm.name === this.dic.EVENTS.eventSendFailed.name || itm.name === this.dic.EVENTS.eventHardBounceGeneral.name || itm.name === this.dic.EVENTS.eventSoftBounce.name),
				read: recipientObj.track_events.find(itm => itm.name === this.dic.EVENTS.eventRead.name),
				acknowledge: recipientObj.track_events.find(itm => itm.name === this.dic.EVENTS.eventAcknowledge.name),
				quizResult,
				attachment: recipientObj.track_events.find(itm => itm.name === this.dic.EVENTS.eventDownloaded.name),
				link: recipientObj.track_events.find(itm => itm.name === this.dic.EVENTS.eventLinkClicked.name),
				login: recipientObj.track_events.find(itm => itm.name === this.dic.EVENTS.eventTsLogin.name),
				userReported: recipientObj.track_events.find(itm => itm.name === this.dic.EVENTS.eventTsReport.name),
			};

			if ((quizResult || recipientInfo.acknowledge) && (!recipientInfo.sent || !recipientInfo.read)) {
				recipientInfo.sent = recipientInfo.sent || {sent: true};
				recipientInfo.read = recipientInfo.read || {read: true};
			}

			this.recipientsEventsTable.recipients.push(recipientInfo);
		});

		this.recipientsEventsTable.headers = [
			{text:'Email', sortKey: 'email', width: 'clamp(215px, 15vw, 260px)'},
			{text:'Sent', sortKey: 'sent', width: '110px', tooltip: this.dic.EVENTS.eventSent.desc},
			{text:'Read', sortKey: 'read', width: '110px', tooltip: this.dic.EVENTS.eventRead.desc},
			{text: this.isCampaignType(this.currentCampaign, this.dic.CONSTANTS.threatSimulationType.attachments.value) ?
					'Attachment Downloaded' : 'Link Clicked', width: '120px', sortKey: 'link', tooltip: 'Indicates that the recipient has clicked on a link inside the email or opened an attachment'},
		];
		if (this.isShowAcknowledge(this.currentCampaign)) {
			this.recipientsEventsTable.headers.push({text:'Acknowledge', width: '140px', sortKey: 'acknowledge', tooltip: this.dic.EVENTS.eventAcknowledge.desc});
		}
		if (this.isShowQuizResults(this.currentCampaign)) {
			this.recipientsEventsTable.headers.push({text:'Quiz Result', width: '110px', sortKey: 'quizResult.score', tooltip: this.dic.EVENTS.eventQuiz.desc});
		}

		this.recipientsEventsTable.cells = [
			{html: 'emailCell'},
			{html: 'sentCell'},
			{html: 'readCell'},
			{html: 'linkCell'},
		];
		if (this.isShowAcknowledge(this.currentCampaign)) {
			this.recipientsEventsTable.cells.push({html: 'acknowledgeCell'});
		}
		if (this.isShowQuizResults(this.currentCampaign)) {
			this.recipientsEventsTable.cells.push({html: 'quizResultCell'});
		}

		if (this.isCampaignType(this.currentCampaign, this.dic.CONSTANTS.threatSimulationType.login.value)) {
			this.recipientsEventsTable.headers.push({text:'Phished Login', width: '150px', sortKey: 'login', tooltip: this.dic.EVENTS.eventTsLogin.desc});
			this.recipientsEventsTable.cells.push({html: 'phishedLoginCell'});
		}
		if (this.isCampaignType(this.currentCampaign, this.dic.CONSTANTS.threatSimulationType.training.value) ||
			this.isCampaignType(this.currentCampaign, this.dic.CONSTANTS.threatSimulationType.login.value) ||
			this.isCampaignType(this.currentCampaign, this.dic.CONSTANTS.threatSimulationType.attachments.value)) {
			this.recipientsEventsTable.headers.push({text:'User Reported', sortKey: 'userReported', width: '120px', tooltip: this.dic.EVENTS.eventTsReport.desc});
			this.recipientsEventsTable.cells.push({html: 'reportedCell'});
		}

		this.recipientsEventsTable.showActions = true;
	}

	prepareGeneralEventsGraph() {
		this.campaignSummary.recipients?.forEach((recipientObj) => {
			const recipientTracking = {sent: 0, read: 0, userReported: 0, link: 0, attachment: 0, acknowledge: 0, login: 0, quiz: 0};
			if (recipientObj.track_events.find(itm => itm.name === this.dic.EVENTS.eventSent.name)) {
				recipientTracking.sent = 1;
			}
			if (recipientObj.track_events.find(itm => itm.name === this.dic.EVENTS.eventRead.name)) {
				recipientTracking.sent = 1;
				recipientTracking.read = 1;
			}
			if (recipientObj.track_events.find(itm => itm.name === this.dic.EVENTS.eventTsReport.name)) {
				recipientTracking.sent = 1;
				recipientTracking.read = 1;
				recipientTracking.userReported = 1;
			}
			if (recipientObj.track_events.find(itm => itm.name === this.dic.EVENTS.eventLinkClicked.name)) {
				recipientTracking.sent = 1;
				recipientTracking.read = 1;
				recipientTracking.link = 1;
			}
			if (recipientObj.track_events.find(itm => itm.name === this.dic.EVENTS.eventDownloaded.name)) {
				recipientTracking.sent = 1;
				recipientTracking.read = 1;
				recipientTracking.attachment = 1;
			}
			if (recipientObj.track_events.find(itm => itm.name === this.dic.EVENTS.eventTsLogin.name)) {
				recipientTracking.sent = 1;
				recipientTracking.read = 1;
				recipientTracking.login = 1;
			}
			if (recipientObj.track_events.find(itm => itm.name === this.dic.EVENTS.eventAcknowledge.name)) {
				recipientTracking.sent = 1;
				recipientTracking.read = 1;
				recipientTracking.acknowledge = 1;
			}
			if (recipientObj.track_events.find(itm => itm.name === this.dic.EVENTS.eventQuiz.name)) {
				recipientTracking.sent = 1;
				recipientTracking.read = 1;
				recipientTracking.quiz = 1;
			}
		});
	}


	isCampaignType = (campaignObj, type) => {
		if (campaignObj.ts_type === type || campaignObj.landing_page_type === type) {
			return true;
		}

		// campaign with custom template (which doesn't have ts_type) will be treated as training campaign
		if (!campaignObj.ts_type && type === this.dic.CONSTANTS.threatSimulationType.training.value) {
			return true;
		}
		//

		if (campaignObj.campaigns?.length) {
			for (let i = 0; i < campaignObj.campaigns.length; i++) {
				if (campaignObj.campaigns[i].ts_type === type || campaignObj.campaigns[i].landing_page_type === type) {
					return true;
				}
				if (!campaignObj.ts_type && type === this.dic.CONSTANTS.threatSimulationType.training.value) {
					return true;
				}
			}
		}

		return false;
	}

	isShowQuizResults = (campaignObj) => {
		if (campaignObj.ts_type === this.dic.CONSTANTS.threatSimulationType.quiz.value || campaignObj.landing_page_type === this.dic.CONSTANTS.threatSimulationType.quiz.value || campaignObj.linked_quiz_id) {
			return true;
		}

		if (campaignObj.campaigns?.length) {
			for (let i = 0; i < campaignObj.campaigns.length; i++) {
				if (campaignObj.campaigns[i].ts_type === this.dic.CONSTANTS.threatSimulationType.quiz.value ||
					campaignObj.campaigns[i].landing_page_type === this.dic.CONSTANTS.threatSimulationLandingPageType.quiz ||
					campaignObj.campaigns[i].linked_quiz_id) {
					return true;
				}
			}
		}

		return false;
	}

	isShowAcknowledge = (campaignObj) => {
		// we do not show "acknowledge" if all campaigns are "training" with "quiz landing pages"
		let areAllLandingPagesQuiz = this.currentCampaign.landing_page_type === this.dic.CONSTANTS.threatSimulationLandingPageType.quiz || !!this.currentCampaign.linked_quiz_id;
		if (!areAllLandingPagesQuiz && this.currentCampaign.campaigns?.length) {
			areAllLandingPagesQuiz = this.currentCampaign.campaigns.every(campaign => {
				return this.isCampaignType(campaign, this.dic.CONSTANTS.threatSimulationType.training.value) &&
					campaign.landing_page_type === this.dic.CONSTANTS.threatSimulationLandingPageType.quiz;
			});
		}

		return !areAllLandingPagesQuiz &&
			(this.isCampaignType(this.currentCampaign, this.dic.CONSTANTS.threatSimulationType.login.value) ||
			this.isCampaignType(this.currentCampaign, this.dic.CONSTANTS.threatSimulationType.attachments.value) ||
			this.isCampaignType(this.currentCampaign, this.dic.CONSTANTS.threatSimulationType.materials.value) ||
			this.isCampaignType(this.currentCampaign, this.dic.CONSTANTS.threatSimulationType.training.value));
	}

	searchRecipient = (event) => {
		this.recipientsEventsTable.recipients.forEach(record => {
			// search
			if (event.searchTerm) {
				const isFound = searchTextExecute(record, event.searchTerm);
				if (!isFound) {
					record.hide = true;
					return;
				}
			}

			if (event.activeFilters) {
				// need to match all filter types
				let numFilterToMatch = Object.keys(event.activeFilters).length;

				if (event.activeFilters['status']?.length) {
					if (event.activeFilters['status'].includes(record.status)) {
						numFilterToMatch--;
					}
				}

				if (numFilterToMatch) {
					record.hide = true;
					return;
				}
			}

			record.hide = false;
		});
	};

	openLocation = (ll) => {
		this.gs.openLocation(ll);
	};

	showRecipientSummaryActions = () => {
		return ['Resend training materials', 'Acknowledge reminder'];
	}

	selectRecipientAction = (recipientObj, action) => {
		switch (action) {
			case 'Resend training materials':
				this.sendCampaignTraining([recipientObj]);
				break;

			case 'Acknowledge reminder':
				this.sendAcknowledgeReminder([recipientObj]);
				break;
		}
	}

	showRecipientBulkActions = () => {
		return ['Resend training materials', 'Acknowledge reminder'];
	}

	selectRecipientBulkAction = (recipients, action) => {
		switch (action) {
			case 'Resend training materials':
				this.sendCampaignTraining(recipients);
				break;

			case 'Acknowledge reminder':
				this.sendAcknowledgeReminder(recipients);
				break;
		}
	}

	sendAcknowledgeReminder = (recipients) => {
		if (!recipients?.length) {
			return;
		}

		this.gs.showPopup({
			title: 'Acknowledge Reminder',
			subTitle: 'A reminder will be sent to click on the "Acknowledge" button in the campaign landing page',
			body: [],
			type: this.dic.CONSTANTS.popupWarning,
			doneBtnText: 'Confirm',
			doneCb: () => {
				const data = {
					action: 'sendAcknowledgeReminder',
					recipients: recipients.map(itm => itm.email)
				};

				this.rs.updateThreatSimulationCampaign(this.currentCampaign._id, data).then(() => {
					this.ns.showInfoMessage(this.dic.MESSAGES.operationsSuccess);
				});
			}
		});
	}

	sendCampaignTraining = (recipients) => {
		if (!recipients?.length) {
			return;
		}

		this.gs.showPopup({
			title: 'Resend Training Materials',
			subTitle: 'All selected recipients will receive the training material again',
			body: [],
			type: this.dic.CONSTANTS.popupWarning,
			doneBtnText: 'Confirm',
			doneCb: () => {
				const data = {
					action: 'sendCampaignTraining',
					recipients: recipients.map(itm => itm.email)
				};

				this.rs.updateThreatSimulationCampaign(this.currentCampaign._id, data).then(() => {
					this.ns.showInfoMessage(this.dic.MESSAGES.operationsSuccess);
				});
			}
		});
	}

	exportRecipientEventsToCsv = (sortBy) => {
		if (!this.recipientsEventsTable.recipients.length) {
			this.ns.showWarnMessage(this.dic.ERRORS.noDataToExportCsv);
			return;
		}

		let csvString = `Email,Sent,Read,Link Clicked`;

		if (this.isCampaignType(this.currentCampaign, this.dic.CONSTANTS.threatSimulationType.training.value) ||
			this.isCampaignType(this.currentCampaign, this.dic.CONSTANTS.threatSimulationType.login.value) ||
			this.isCampaignType(this.currentCampaign, this.dic.CONSTANTS.threatSimulationType.attachments.value)) {
			csvString += ',User Reported';
		}
		if (this.isCampaignType(this.currentCampaign, this.dic.CONSTANTS.threatSimulationType.attachments.value)) {
			csvString += ',Attachment Downloaded';
		}
		if (this.isCampaignType(this.currentCampaign, this.dic.CONSTANTS.threatSimulationType.login.value)) {
			csvString += ',Phished Login';
		}
		if (this.isShowAcknowledge(this.currentCampaign)) {
			csvString += ',Acknowledge';
		}
		if (this.isShowQuizResults(this.currentCampaign)) {
			csvString += ',Quiz Results';
		}
		if (!this.isTrainingCampaign) {
			csvString += ',Status';
		}
		csvString += '\n';

		let sortedTable = _.filter(this.recipientsEventsTable.recipients,incident => {return !incident.hide});
		if (sortBy) {
			sortedTable = this.gs.sortTable(sortedTable, sortBy);
		}

		sortedTable.forEach((recipientObj) => {
			let linkEvent = '';
			if (recipientObj.link) {
				if (recipientObj.link.agent?.browser) {
					linkEvent = `${recipientObj.link.agent.browser.name}, ${recipientObj.link.agent.os.name} ${recipientObj.link.agent.os.version}`;
				}
				if (recipientObj.link.ip) {
					linkEvent += `, ${recipientObj.link.ip}`;
				}
			}
			else {
				linkEvent = 'False';
			}

			let attachmentEvent = '';
			if (recipientObj.attachment) {
				if (recipientObj.attachment.agent?.browser) {
					attachmentEvent = `${recipientObj.attachment.agent.browser.name}, ${recipientObj.attachment.agent.os.name} ${recipientObj.attachment.agent.os.version}`;
				}
				if (recipientObj.attachment.ip) {
					attachmentEvent += `, ${recipientObj.attachment.ip}`;
				}
			}
			else {
				attachmentEvent = 'False';
			}

			let sentCreated = 'Not sent';
			if (recipientObj.sent?.created) {
				const created = new Date(recipientObj.sent.created);
				sentCreated = `Sent: ${created.toLocaleDateString()} ${created.toLocaleTimeString('en-US', {
					hour: 'numeric',
					minute: 'numeric',
					hour12: false
				})}`;
			}

			let readCreated = 'Not read';
			if (recipientObj.read?.created) {
				const created = new Date(recipientObj.read.created);
				readCreated = `Read: ${created.toLocaleDateString()} ${created.toLocaleTimeString('en-US', {
					hour: 'numeric',
					minute: 'numeric',
					hour12: false
				})}`;
			}

			csvString += `${recipientObj.email},"${sentCreated}","${readCreated}","${linkEvent}"`;

			if (this.isCampaignType(this.currentCampaign, this.dic.CONSTANTS.threatSimulationType.training.value) ||
				this.isCampaignType(this.currentCampaign, this.dic.CONSTANTS.threatSimulationType.login.value) ||
				this.isCampaignType(this.currentCampaign, this.dic.CONSTANTS.threatSimulationType.attachments.value)) {
				csvString += `,"${recipientObj.userReported ? 'True' : 'False'}"`;
			}
			if (this.isCampaignType(this.currentCampaign, this.dic.CONSTANTS.threatSimulationType.attachments.value)) {
				csvString += `,"${attachmentEvent}"`;
			}
			if (this.isCampaignType(this.currentCampaign, this.dic.CONSTANTS.threatSimulationType.login.value)) {
				csvString += `,${recipientObj.login ? 'True' : 'False'}`;
			}
			if (this.isShowAcknowledge(this.currentCampaign)) {
				csvString += `,${recipientObj.acknowledge ? 'True' : 'False'}`;
			}
			if (this.isShowQuizResults(this.currentCampaign)) {
				csvString += `,${recipientObj.quizResult ? recipientObj.quizResult.score + '%' : 'Unsubmitted'}`;
			}
			if (!this.isTrainingCampaign) {
				csvString += `,${recipientObj.status}`;
			}
			csvString += '\n';
		});

		this.gs.exportCsv(csvString, `recipients_events.csv`);
	};
}

function searchTextExecute(recipientObj, searchTerm) {
	return (recipientObj.email.toLowerCase().indexOf(searchTerm) > -1);
}
